(() => {
  if (window.wlyHeaderReady) return;
  window.wlyHeaderReady = true;
  const wlyRoot = document.documentElement;
  const wlyReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wlyDrawerTime = 320;
  const wlyState = { lastFocus: null, searchTimer: null, searchController: null, searchCache: new Map() };
  const wlyQ = (wlySelector, wlyScope = document) => wlyScope.querySelector(wlySelector);
  const wlyQA = (wlySelector, wlyScope = document) => Array.from(wlyScope.querySelectorAll(wlySelector));
  const wlyHeader = () => wlyQ('[data-wly-header]');
  const wlyIsVisible = (wlyEl) => !!wlyEl && wlyEl.getClientRects().length > 0;
  const wlyFocusable = (wlyScope) => wlyQA('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])', wlyScope).filter(wlyIsVisible);
  const wlyLock = (wlyOn) => wlyRoot.classList.toggle('wly-scroll-lock', wlyOn);
  const wlyFormatMoney = (wlyCents, wlyFormat) => {
    const wlyTemplate = (wlyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const wlyMatch = wlyTemplate.match(/\{\{\s*(\w+)\s*\}\}/);
    const wlyKey = wlyMatch ? wlyMatch[1] : 'amount';
    const wlyNoDecimals = wlyKey.indexOf('no_decimals') > -1;
    const wlyComma = wlyKey.indexOf('comma_separator') > -1;
    const wlyFixed = (wlyCents / 100).toFixed(wlyNoDecimals ? 0 : 2).split('.');
    const wlyWhole = wlyFixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, wlyComma ? '.' : ',');
    const wlyValue = wlyFixed[1] ? `${wlyWhole}${wlyComma ? ',' : '.'}${wlyFixed[1]}` : wlyWhole;
    return wlyTemplate.replace(/\{\{\s*\w+\s*\}\}/, wlyValue);
  };
  const wlyTrap = (wlyEvent, wlyScope) => {
    if (wlyEvent.key !== 'Tab') return;
    const wlyItems = wlyFocusable(wlyScope);
    if (!wlyItems.length) return;
    const wlyFirst = wlyItems[0];
    const wlyLast = wlyItems[wlyItems.length - 1];
    if (wlyEvent.shiftKey && document.activeElement === wlyFirst) {
      wlyEvent.preventDefault();
      wlyLast.focus();
    } else if (!wlyEvent.shiftKey && document.activeElement === wlyLast) {
      wlyEvent.preventDefault();
      wlyFirst.focus();
    }
  };
  const wlyDrawerOpen = () => {
    const wlyHead = wlyHeader();
    const wlyDrawer = wlyHead && wlyQ('[data-wly-drawer]', wlyHead);
    if (!wlyDrawer) return;
    wlyState.lastFocus = document.activeElement;
    wlyDrawer.classList.add('is-open');
    wlyQ('.wly-header__overlay--drawer', wlyHead)?.classList.add('is-open');
    wlyQA('[data-wly-drawer-open]', wlyHead).forEach((wlyBtn) => wlyBtn.setAttribute('aria-expanded', 'true'));
    wlyLock(true);
    window.setTimeout(() => wlyDrawer.focus({ preventScroll: true }), 60);
  };
  const wlyDrawerClose = (wlyReturnFocus = true) => {
    const wlyHead = wlyHeader();
    const wlyDrawer = wlyHead && wlyQ('[data-wly-drawer]', wlyHead);
    if (!wlyDrawer || !wlyDrawer.classList.contains('is-open')) return false;
    wlyDrawer.classList.remove('is-open');
    wlyQ('.wly-header__overlay--drawer', wlyHead)?.classList.remove('is-open');
    wlyQA('[data-wly-drawer-open]', wlyHead).forEach((wlyBtn) => wlyBtn.setAttribute('aria-expanded', 'false'));
    wlyLock(false);
    if (wlyReturnFocus && wlyState.lastFocus) wlyState.lastFocus.focus({ preventScroll: true });
    return true;
  };
  const wlySearchOpen = () => {
    const wlyHead = wlyHeader();
    const wlySearch = wlyHead && wlyQ('[data-wly-search]', wlyHead);
    if (!wlySearch) return false;
    const wlyWasDrawer = wlyDrawerClose(false);
    if (!wlyWasDrawer) wlyState.lastFocus = document.activeElement;
    const wlyBarHeight = Math.round(wlyHead.getBoundingClientRect().bottom);
    if (wlyBarHeight > 40) wlySearch.style.setProperty('--wly-search-bar-height', `${wlyBarHeight}px`);
    wlySearch.classList.add('is-open');
    wlyQ('.wly-header__overlay--search', wlyHead)?.classList.add('is-open');
    wlyQA('[data-wly-search-open]', wlyHead).forEach((wlyBtn) => wlyBtn.setAttribute('aria-expanded', 'true'));
    wlyLock(true);
    const wlyInput = wlyQ('[data-wly-search-input]', wlySearch);
    window.setTimeout(() => wlyInput?.focus({ preventScroll: true }), 60);
    return true;
  };
  const wlySearchClose = () => {
    const wlyHead = wlyHeader();
    const wlySearch = wlyHead && wlyQ('[data-wly-search]', wlyHead);
    if (!wlySearch || !wlySearch.classList.contains('is-open')) return false;
    wlySearch.classList.remove('is-open');
    wlyQ('.wly-header__overlay--search', wlyHead)?.classList.remove('is-open');
    wlyQA('[data-wly-search-open]', wlyHead).forEach((wlyBtn) => wlyBtn.setAttribute('aria-expanded', 'false'));
    wlyLock(false);
    if (wlyState.lastFocus && wlyIsVisible(wlyState.lastFocus)) wlyState.lastFocus.focus({ preventScroll: true });
    return true;
  };
  const wlySearchStrings = () => {
    try {
      return JSON.parse(wlyQ('[data-wly-search-strings]')?.textContent || '{}');
    } catch (wlyErr) {
      return {};
    }
  };
  const wlyEscape = (wlyText) => String(wlyText).replace(/[&<>"']/g, (wlyChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[wlyChar]));
  const wlyRenderSearch = (wlySearch, wlyTerms, wlyHtml) => {
    const wlyResults = wlyQ('[data-wly-search-results]', wlySearch);
    if (!wlyResults) return;
    const wlyStrings = wlySearchStrings();
    const wlyForm = wlyQ('form', wlySearch);
    const wlyParams = new URLSearchParams(new FormData(wlyForm));
    wlyParams.set('q', wlyTerms);
    const wlyAllUrl = `${wlyForm.getAttribute('action')}?${wlyParams.toString()}`;
    if (wlyHtml === null) {
      wlyResults.innerHTML = `<p class="wly-header__search-message">${wlyEscape(wlyStrings.error || '')}</p>`;
    } else {
      const wlyDoc = new DOMParser().parseFromString(wlyHtml, 'text/html');
      const wlyBlock = wlyQ('.wly-search-results', wlyDoc);
      const wlyCount = wlyBlock ? parseInt(wlyBlock.dataset.wlySearchCount, 10) || 0 : 0;
      if (wlyCount > 0) {
        wlyResults.innerHTML = `${wlyBlock.innerHTML}${wlyStrings.viewAll ? `<a class="wly-header__search-viewall" href="${wlyEscape(wlyAllUrl)}">${wlyEscape(wlyStrings.viewAll)}</a>` : ''}`;
      } else {
        wlyResults.innerHTML = `<p class="wly-header__search-message">${wlyEscape((wlyStrings.empty || '').replace('[terms]', wlyTerms))}</p>`;
      }
    }
    wlyQA('[data-wly-search-type]', wlyResults).forEach((wlyTag) => {
      wlyTag.textContent = wlyStrings[wlyTag.getAttribute('data-wly-search-type')] || '';
      if (!wlyTag.textContent) wlyTag.remove();
    });
    const wlyStatus = wlyQ('[data-wly-search-status]', wlySearch);
    if (wlyStatus) wlyStatus.hidden = true;
    wlyResults.hidden = false;
    wlyResults.removeAttribute('aria-busy');
  };
  const wlySearchFetch = (wlySearch, wlyTerms) => {
    const wlyResults = wlyQ('[data-wly-search-results]', wlySearch);
    const wlyStatus = wlyQ('[data-wly-search-status]', wlySearch);
    if (!wlyTerms) {
      if (wlyStatus) wlyStatus.hidden = true;
      if (wlyState.searchController) wlyState.searchController.abort();
      if (wlyResults) {
        wlyResults.hidden = true;
        wlyResults.innerHTML = '';
      }
      return;
    }
    if (wlyState.searchCache.has(wlyTerms)) {
      wlyRenderSearch(wlySearch, wlyTerms, wlyState.searchCache.get(wlyTerms));
      return;
    }
    if (wlyState.searchController) wlyState.searchController.abort();
    wlyState.searchController = new AbortController();
    if (wlyResults && !wlyResults.hidden) wlyResults.setAttribute('aria-busy', 'true');
    else if (wlyStatus) wlyStatus.hidden = false;
    const wlyParams = new URLSearchParams({
      q: wlyTerms,
      section_id: wlySearch.dataset.section || 'wly-predictive-search',
      'resources[type]': wlySearch.dataset.types || 'product',
      'resources[limit]': wlySearch.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor',
    });
    fetch(`${wlySearch.dataset.url || '/search/suggest'}?${wlyParams.toString()}`, { signal: wlyState.searchController.signal })
      .then((wlyResponse) => {
        if (!wlyResponse.ok) throw new Error(String(wlyResponse.status));
        return wlyResponse.text();
      })
      .then((wlyHtml) => {
        wlyState.searchCache.set(wlyTerms, wlyHtml);
        wlyRenderSearch(wlySearch, wlyTerms, wlyHtml);
      })
      .catch((wlyErr) => {
        if (wlyErr.name !== 'AbortError') wlyRenderSearch(wlySearch, wlyTerms, null);
      });
  };
  const wlyStickyHeight = () => {
    const wlyHead = wlyHeader();
    if (!wlyHead) return 0;
    const wlyWrap = wlyHead.closest('.shopify-section') || wlyHead;
    return getComputedStyle(wlyWrap).position === 'sticky' || getComputedStyle(wlyWrap).position === 'fixed' ? wlyWrap.getBoundingClientRect().height : 0;
  };
  const wlyScrollOffset = () => {
    const wlyHead = wlyHeader();
    return wlyStickyHeight() + (wlyHead ? parseInt(wlyHead.dataset.scrollOffset || '8', 10) : 8);
  };
  const wlyFindTarget = (wlyHash) => {
    if (!wlyHash) return null;
    let wlyName = wlyHash;
    try {
      wlyName = decodeURIComponent(wlyHash);
    } catch (wlyErr) {}
    const wlySafe = window.CSS && CSS.escape ? CSS.escape(wlyName) : wlyName.replace(/[^a-zA-Z0-9_-]/g, '');
    const wlyCandidates = wlyQA(`[data-wly-anchor="${wlySafe}"], [id="${wlySafe}"]`);
    return wlyCandidates.find(wlyIsVisible) || null;
  };
  const wlyScrollTo = (wlyTarget, wlyInstant) => {
    const wlyTop = wlyTarget.getBoundingClientRect().top + window.scrollY - wlyScrollOffset();
    window.scrollTo({ top: Math.max(0, wlyTop), behavior: wlyInstant || wlyReduceMotion.matches ? 'auto' : 'smooth' });
  };
  const wlyHandleAnchor = (wlyEvent, wlyLink) => {
    const wlyUrl = new URL(wlyLink.href, window.location.href);
    if (!wlyUrl.hash || wlyUrl.origin !== window.location.origin || wlyUrl.pathname !== window.location.pathname) return;
    const wlyTarget = wlyFindTarget(wlyUrl.hash.slice(1));
    if (!wlyTarget) return;
    wlyEvent.preventDefault();
    const wlyGo = () => {
      wlyScrollTo(wlyTarget, false);
      if (window.history && history.replaceState) history.replaceState(null, '', wlyUrl.hash);
    };
    if (wlyDrawerClose(false)) {
      window.setTimeout(wlyGo, wlyReduceMotion.matches ? 0 : wlyDrawerTime);
    } else {
      wlyGo();
    }
  };
  const wlyAnnounceData = () => {
    try {
      return JSON.parse(wlyQ('[data-wly-announce-data]')?.textContent || 'null');
    } catch (wlyErr) {
      return null;
    }
  };
  const wlyUpdateAnnouncement = (wlyCart) => {
    const wlyAnnounce = wlyQ('[data-wly-announce]');
    const wlyData = wlyAnnounceData();
    if (!wlyAnnounce || !wlyData || !wlyData.threshold) return;
    const wlyTotal = wlyCart.total_price || 0;
    const wlyPercent = Math.min(100, Math.round((wlyTotal / wlyData.threshold) * 100));
    const wlyMessage = wlyQ('[data-wly-announce-message]', wlyAnnounce);
    const wlyTrack = wlyQ('[data-wly-announce-track]', wlyAnnounce);
    wlyAnnounce.style.setProperty('--wly-announce-progress', `${wlyPercent}%`);
    if (wlyTrack) wlyTrack.setAttribute('aria-valuenow', String(wlyPercent));
    if (!wlyMessage) return;
    if (wlyTotal >= wlyData.threshold) {
      wlyMessage.innerHTML = wlyData.unlocked || wlyData.message;
    } else if (wlyTotal > 0 && wlyData.progress) {
      wlyMessage.innerHTML = wlyData.progress.split('[amount]').join(wlyFormatMoney(wlyData.threshold - wlyTotal, wlyData.moneyFormat));
    } else {
      wlyMessage.innerHTML = wlyData.message;
    }
  };
  const wlyUpdateCount = (wlyCount) => {
    wlyQA('[data-wly-cart-link] [data-js-cart-count-mobile]').forEach((wlyEl) => {
      if (wlyEl.getAttribute('data-js-cart-count-mobile') !== String(wlyCount)) {
        wlyEl.setAttribute('data-js-cart-count-mobile', String(wlyCount));
        wlyEl.textContent = String(wlyCount);
      }
    });
    wlyQA('[data-wly-cart-link]').forEach((wlyCartLink) => {
      const wlyLabel = (wlyCartLink.getAttribute('aria-label') || '').split(',')[0];
      wlyCartLink.setAttribute('aria-label', `${wlyLabel}, ${wlyCount}`);
    });
  };
  let wlyCartTimer = null;
  const wlyRefreshCart = () => {
    window.clearTimeout(wlyCartTimer);
    wlyCartTimer = window.setTimeout(() => {
      const wlyBase = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(`${wlyBase}cart.js`, { headers: { Accept: 'application/json' } })
        .then((wlyResponse) => wlyResponse.json())
        .then((wlyCart) => {
          wlyUpdateCount(wlyCart.item_count);
          wlyUpdateAnnouncement(wlyCart);
        })
        .catch(() => {});
    }, 150);
  };
  document.addEventListener('click', (wlyEvent) => {
    const wlyTarget = wlyEvent.target;
    if (wlyTarget.closest('[data-wly-drawer-open]')) {
      wlyEvent.preventDefault();
      wlyDrawerOpen();
      return;
    }
    if (wlyTarget.closest('[data-wly-drawer-close]')) {
      wlyEvent.preventDefault();
      wlyDrawerClose();
      return;
    }
    if (wlyTarget.closest('[data-wly-search-open]')) {
      if (wlySearchOpen()) wlyEvent.preventDefault();
      return;
    }
    if (wlyTarget.closest('[data-wly-search-close]')) {
      wlyEvent.preventDefault();
      wlySearchClose();
      return;
    }
    const wlyLink = wlyTarget.closest('a[href*="#"]');
    if (wlyLink && !wlyLink.hasAttribute('data-js-popup-button') && !wlyEvent.defaultPrevented && !wlyEvent.metaKey && !wlyEvent.ctrlKey && !wlyEvent.shiftKey) {
      wlyHandleAnchor(wlyEvent, wlyLink);
    }
  });
  document.addEventListener('keydown', (wlyEvent) => {
    const wlyHead = wlyHeader();
    if (!wlyHead) return;
    const wlyDrawer = wlyQ('[data-wly-drawer].is-open', wlyHead);
    const wlySearch = wlyQ('[data-wly-search].is-open', wlyHead);
    if (wlyEvent.key === 'Escape') {
      if (wlySearch) wlySearchClose();
      else if (wlyDrawer) wlyDrawerClose();
      return;
    }
    if (wlySearch) wlyTrap(wlyEvent, wlySearch);
    else if (wlyDrawer) wlyTrap(wlyEvent, wlyDrawer);
  });
  document.addEventListener('input', (wlyEvent) => {
    const wlyInput = wlyEvent.target.closest('[data-wly-search-input]');
    if (!wlyInput) return;
    const wlySearch = wlyInput.closest('[data-wly-search]');
    window.clearTimeout(wlyState.searchTimer);
    const wlyTerms = wlyInput.value.trim();
    const wlyStatus = wlyQ('[data-wly-search-status]', wlySearch);
    const wlyResults = wlyQ('[data-wly-search-results]', wlySearch);
    if (wlyTerms && wlyStatus && wlyResults && wlyResults.hidden && !wlyState.searchCache.has(wlyTerms)) wlyStatus.hidden = false;
    wlyState.searchTimer = window.setTimeout(() => wlySearchFetch(wlySearch, wlyTerms), 200);
  });
  ['theme:cart::added', 'theme:cart::removed', 'cart:updated', 'cart:refresh', 'rebuy:cart.change', 'rebuy:cart.add'].forEach((wlyName) => {
    document.addEventListener(wlyName, wlyRefreshCart);
  });
  document.addEventListener('wly:cart:updated', (wlyEvent) => {
    const wlyCart = wlyEvent.detail && wlyEvent.detail.cart;
    if (!wlyCart) return;
    window.clearTimeout(wlyCartTimer);
    wlyUpdateCount(wlyCart.item_count);
    wlyUpdateAnnouncement(wlyCart);
  });
  const wlyWatchCount = () => {
    const wlyCount = wlyQ('[data-wly-cart-link] .wly-header__cart-count');
    if (!wlyCount || wlyCount.dataset.wlyWatched) return;
    wlyCount.dataset.wlyWatched = 'true';
    new MutationObserver(() => wlyRefreshCart()).observe(wlyCount, { attributes: true, attributeFilter: ['data-js-cart-count-mobile'] });
  };
  const wlyBoot = () => {
    wlyWatchCount();
    const wlyHash = window.location.hash.slice(1);
    if (wlyHash) {
      const wlyTarget = wlyFindTarget(wlyHash);
      if (wlyTarget && !document.getElementById(wlyHash)) {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        let wlyDone = false;
        const wlyGo = () => {
          if (wlyDone) return;
          wlyDone = true;
          window.setTimeout(() => wlyScrollTo(wlyTarget, false), 250);
        };
        if (document.readyState === 'complete') {
          wlyGo();
        } else {
          window.addEventListener('load', wlyGo, { once: true });
          window.setTimeout(wlyGo, 1500);
        }
      }
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wlyBoot);
  } else {
    wlyBoot();
  }
  window.addEventListener('pageshow', (wlyEvent) => {
    if (wlyEvent.persisted) wlyRefreshCart();
  });
  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 990px)').matches) wlyDrawerClose(false);
  });
  document.addEventListener('shopify:section:load', () => {
    wlyLock(false);
    wlyBoot();
  });
})();
